import React, { useCallback, useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'
import Breadcrumb from '../../components/Navigation/Breadcrumb'
import TestNavigation from '../../components/Navigation/TestNavigation'
import appRoutes from '../../statics/appRoutes'
import { httpGet } from '../../store/services/testServiceClient'
import { formatDate, toRelativeDateTime } from '../../utils/dateTime.js'
import Scenarios from './components/Scenarios.jsx'
import ErrorBoundary from '../../components/ErrorBoundary.jsx'
import Alert from '../../components/Alerts/Alert'
import { useDispatch, useSelector } from 'react-redux'
import { getTest, getTestSuccess } from '../../store/actions'
import { Button, Empty, Skeleton, Tag } from 'antd'
import { getTagColor } from '../../utils/util'

const Test = (props) => {
  const testIndexPath = `projects/${props.match.params.project}/testindex/${props.match.params.testId}`
  const testPath = `projects/${props.match.params.project}/tests/${props.match.params.testId}`
  const projectId = props.match.params.project
  const alertbox = useRef()
  const [test, setTest] = useState()
  const [assignee, setAssignee] = useState('')
  const isLoadingTest = useSelector((state) => state.test.loading)
  const dispatch = useDispatch()

  const showError = useCallback((message, e) => {
    alertbox.current?.showServerError(message, e)
    scrollToElement('alert_box')
  }, [])

  const showTestExecutions = () => {
    props.history.push(
      `/p/${props.match.params.project}/executions?testName=${test.Name}`
    )
  }

  const loadTest = useCallback(async () => {
    try {
      dispatch(getTest())
      setTest((await httpGet(testIndexPath)).data)
    } catch (e) {
      showError('Error loading test details.', e)
    } finally {
      dispatch(getTestSuccess())
    }
  }, [dispatch, showError, testIndexPath, testPath])

  // Some scenarios may not have value for all the parameters
  // Populate the scenario arguments (argsVM) in the order of parameters array

  const getAssignee = useCallback(async () => {
    try {
      let assigneeName = 'Nobody'
      if (test.Assignee) {
        const response = await httpGet(
          `projects/${props.match.params.project}/users`
        )
        const found = response.data.find((user) => user.Id === test.Assignee)
        if (found) {
          assigneeName = !found.Name ? found.Email : found.Name
        }
      }
      return assigneeName
    } catch (e) {
      showError('Error loading project users.', e)
    }
  }, [props.match.params.project, showError, test])

  const scrollToElement = (elementId) => {
    const ele = document.getElementById(elementId)
    ele?.scrollIntoView()
  }

  const launchReport = (runId) => {
    props.history.push(`/p/${props.match.params.project}/executions/${runId}`)
    // window.open(`${window.location.origin}/report?runId=${runId}`, '_blank')
  }

  // const extractQueryParameterFromUrl = (paramName) => {
  //   const uri = window.location.search.substring(1)
  //   const params = new URLSearchParams(uri)
  //   if (params.has(paramName)) {
  //     return params.get(paramName)
  //   } else {
  //     throw new Error(`Query parameter '${paramName}' is missing.`)
  //   }
  // }

  const getCommaSeperatedTags = (tags) => {
    return !tags ? '' : tags.toString()
  }

  const formatRelativeDate = (date) => {
    return toRelativeDateTime(date)
  }

  const formatDuration = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000)
    const seconds = ((milliseconds % 60000) / 1000).toFixed(0)

    if (minutes <= 0 && seconds < 1) {
      return 'less than a second'
    } else {
      const delimiter = minutes > 0 && seconds > 0 ? ',' : ''
      const minutesSuffix = minutes === 1 ? 'min' : 'mins'
      const minutesText =
        minutes > 0 ? `${minutes} ${minutesSuffix}${delimiter}` : ''
      const secondsSuffix = seconds === 1 ? 'sec' : 'secs'
      const secondsText = seconds > 0 ? `${seconds} ${secondsSuffix}` : ''
      return `${minutesText} ${secondsText}`
    }
  }

  useEffect(() => {
    loadTest()
  }, [loadTest])

  useEffect(() => {
    const loadUsersAndSetAssignee = async () => {
      if (test) {
        setAssignee(await getAssignee())
      }
    }
    loadUsersAndSetAssignee()
  }, [test?.Assignee, getAssignee, test])

  return (
    <>
      <div className='mt-3'>
        <Breadcrumb testName={test?.Name} />
        <TestNavigation />
        <Alert showCloseButton ref={alertbox} />
        {isLoadingTest === true && (
          <div className='w-full 2xl:w-4/5 shadow-sm px-4 pt-2 pb-12 '>
            <Skeleton active />
          </div>
        )}
        {isLoadingTest === false && test != null && (
          <div className='mt-3'>
            {props.match.path === appRoutes.testPage && (
              <div
                id='testDetailsCard'
                className='w-full 2xl:w-4/5 shadow-sm pb-2'
              >
                <div className='px-4 flex justify-start items-center h-12 bg-transparentColor font-bold text-md w-full'>
                  <b className='pr-2'>Test: </b> {test.Name}
                </div>
                <div className='p-4 md:w-96 lg:w-1/2'>
                  <div className='grid grid-cols-2 gap-y-3'>
                    <label id='testStatus'>Status:</label>
                    <label id='testStatusValue' htmlFor='testStatus'>
                      {test.Status}
                    </label>
                    <label id='testAssignee'>Assignee:</label>
                    <label id='testAssigneeValue' htmlFor='testAssignee'>
                      {assignee}
                    </label>
                    {test.Tags?.length > 0 && (
                      <>
                        <label id='testTags'>Tags:</label>
                        <label id='testTagsValue' htmlFor='testTags'>
                          {getCommaSeperatedTags(test.Tags)}
                        </label>
                      </>
                    )}
                    <label id='testDate'>Last modified:</label>
                    <label id='testDateValue' htmlFor='testDate'>
                      {formatRelativeDate(test.LastModified)}
                    </label>
                    {test.Description?.trim().length > 0 && (
                      <>
                        <label id='testDescription'>Description:</label>
                        <label
                          id='testDescriptionValue'
                          htmlFor='testDescription'
                        >
                          {test.Description}
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {props.match.path === appRoutes.testExecutionsPage && (
              <div className='px-0 w-full 2xl:w-4/5 shadow-sm pb-2'>
                <div className='px-4 h-12 bg-transparentColor font-bold text-md w-full flex justify-between items-center'>
                  <b>Recent Executions </b>
                  {test.RecentExecutions?.length > 0 && (
                    <Button type='ghost' onClick={showTestExecutions}>
                      Show all test executions
                    </Button>
                  )}
                </div>
                <div className='p-4'>
                  {test.RecentExecutions?.length > 0 ? (
                    <div className='flex flex-col'>
                      <div className='-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
                        <div className='py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8'>
                          <div className='shadow overflow-hidden border-b border-gray-200 sm:rounded-sm'>
                            <table className='min-w-full divide-y divide-gray-200'>
                              <thead className='bg-gray-50'>
                                <tr>
                                  <th
                                    scope='col'
                                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider'
                                  >
                                    Run At
                                  </th>
                                  <th
                                    scope='col'
                                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider'
                                  >
                                    Duration
                                  </th>
                                  <th
                                    scope='col'
                                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider'
                                  >
                                    Result
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='bg-white divide-y divide-gray-200'>
                                {test.RecentExecutions.sort(
                                  (a, b) =>
                                    new Date(b.StartedAt).getTime() -
                                    new Date(a.StartedAt).getTime()
                                ).map((execution) => (
                                  <tr
                                    onClick={() => launchReport(execution.Id)}
                                    key={execution.Id}
                                    name={execution.Name}
                                    className='cursor-pointer hover:bg-transparentColor'
                                    title='Click to open the report'
                                  >
                                    <td className='px-6 py-4 whitespace-nowrap'>
                                      <div className='text-sm text-gray-900'>
                                        {formatDate(execution.StartedAt)}
                                      </div>
                                    </td>
                                    <td className='px-6 py-4 whitespace-nowrap'>
                                      {formatDuration(
                                        execution.DurationInMilliseconds
                                      )}
                                    </td>
                                    <td className='px-6 py-4 whitespace-nowrap'>
                                      <Tag
                                        color={getTagColor(execution.Result)}
                                      >
                                        {execution.Result === 'DidNotFinish'
                                          ? 'Not Finished'
                                          : execution.Result}
                                      </Tag>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<b>No test executions.</b>}
                    />
                  )}
                </div>
              </div>
            )}
            {props.match.path === appRoutes.testScenariosPage && (
              <ErrorBoundary key={testPath} message='Error loading datasets for this test.'>
                <Scenarios
                  test={test}
                  testPath={testPath}
                  projectId={projectId}
                  setTest={setTest}
                />
              </ErrorBoundary>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default withRouter(Test)
